import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Popconfirm, Space, Table, Tag, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../../shared/i18n';
import tmService from '../../../services/tmService';
import { fmtTime, ok, showSecretOnce, toastErr } from './common';

const { Text } = Typography;

const ApiKeys = ({ stream, onChange }) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await tmService.listApiKeys(stream.id);
            setRows(Array.isArray(r?.data) ? r.data : []);
        } catch (e) {
            toastErr(e, t('Failed to load'));
        } finally {
            setLoading(false);
        }
    }, [stream.id, t]);

    useEffect(() => { load(); }, [load]);

    const create = async () => {
        const v = await form.validateFields();
        setSaving(true);
        try {
            const r = await tmService.createApiKey(stream.id, v.name.trim());
            if (ok(r)) {
                setOpen(false);
                form.resetFields();
                showSecretOnce({ title: t('API key created'), secret: r.data.key, note: t('It\'s also the SMTP password (username apikey)') });
                load();
                onChange && onChange();
            }
        } catch (e) {
            toastErr(e, t('Creation failed'));
        } finally {
            setSaving(false);
        }
    };

    const revoke = async (k) => {
        try {
            const r = await tmService.revokeApiKey(k.id);
            if (ok(r)) { message.success(t('Revoked')); load(); onChange && onChange(); }
        } catch (e) {
            toastErr(e, t('Operation failed'));
        }
    };

    const columns = [
        { title: t('Name'), dataIndex: 'name' },
        { title: t('Prefix'), dataIndex: 'prefix', render: (v) => <Text code>{v}…</Text> },
        { title: t('Last used'), dataIndex: 'last_used_at', render: fmtTime, responsive: ['md'] },
        { title: t('Created at'), dataIndex: 'created_at', render: fmtTime, responsive: ['md'] },
        {
            title: t('Status'), dataIndex: 'revoked', width: 90,
            render: (v) => (v ? <Tag>{t('Revoked')}</Tag> : <Tag color="green">{t('Valid')}</Tag>),
        },
        {
            title: '', key: 'op', width: 80,
            render: (_, k) => !k.revoked && (
                <Popconfirm title={t('Anything using this key stops working immediately')} okText={t('Revoke')} okButtonProps={{ danger: true }} onConfirm={() => revoke(k)}>
                    <Button type="link" danger size="small">{t('Revoke')}</Button>
                </Popconfirm>
            ),
        },
    ];

    const locked = stream.status === 'banned';

    return (
        <div>
            <Space style={{ marginBottom: 12 }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)} disabled={locked}>{t('Create key')}</Button>
            </Space>
            <Table rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns} pagination={false} scroll={{ x: 560 }} />
            <Modal open={open} title={t('Create key')} onOk={create} confirmLoading={saving} onCancel={() => setOpen(false)} destroyOnClose>
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label={t('Name')} rules={[{ required: true, message: t('Enter a name') }]}>
                        <Input maxLength={64} placeholder="production" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ApiKeys;
